import { useState, useMemo } from "react";
import { MainLayoutContainer } from "components/Layout";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_CAMPAIGN_BY_ID } from "graphql/campaign/campaign.query";
import { GET_SURVEY_BY_ID } from "graphql/survey/survey.query";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  TextField,
  Divider,
  CircularProgress,
} from "@mui/material";
import { SingleChoiceSelector } from "pages/Survey/answer/components";
import { LikertScaleSelector } from "pages/Survey/answer/components/LikertScaleSelector";
import { useNotifications } from "hooks/useNotifications";
import { NotificationSeverity } from "helpers/constants";
import { useMutation } from "@apollo/client";
import { ADD_ANSWER } from "graphql/answer/answer.mutation";
import { useAuthUser } from "hooks/useAuthUser";

type AnswerValue = string | number | number[] | null;
type SurveyAnswers = Record<string, AnswerValue>;

export const CampaignAnswer = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const { notify } = useNotifications();
  const [activeStep, setActiveStep] = useState(0);
  const [allAnswers, setAllAnswers] = useState<Record<string, SurveyAnswers>>({});
  const [addAnswer] = useMutation(ADD_ANSWER);
  const [getSurveyById] = useLazyQuery(GET_SURVEY_BY_ID);

  const { data: campaignData, loading: campaignLoading } = useQuery(
    GET_CAMPAIGN_BY_ID,
    {
      variables: { campaignId },
      skip: !campaignId,
    }
  );

  const campaign = campaignData?.campaign_by_pk;
  const surveys = (campaign?.surveys || []) as Array<{
    survey_id: string;
    survey?: { name: string; survey_id: string };
  }>;
  const currentSurveyId = surveys[activeStep]?.survey_id;

  const { data: surveyData, loading: surveyLoading } = useQuery(GET_SURVEY_BY_ID, {
    variables: { surveyId: currentSurveyId },
    skip: !currentSurveyId,
  });

  const currentSurvey = surveyData?.survey_by_pk;
  const currentAnswers = allAnswers[currentSurveyId || ""] || {};

  const updateAnswer = (questionId: string, value: AnswerValue) => {
    if (!currentSurveyId) return;
    setAllAnswers((prev) => ({
      ...prev,
      [currentSurveyId]: {
        ...prev[currentSurveyId],
        [questionId]: value,
      },
    }));
  };

  const handleNext = () => {
    if (activeStep < surveys.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user?.user_id || !campaignId) {
      notify({
        message: "User information not available",
        severity: NotificationSeverity.error,
      });
      return;
    }

    try {
      const submissionTimestamp = new Date().toISOString();

      // Submit all answers for all surveys
      for (const surveyItem of surveys) {
        const surveyAnswers = allAnswers[surveyItem.survey_id] || {};
        const surveyId = surveyItem.survey_id;

        // Get survey data to find questions
        const result = await getSurveyById({
          variables: { surveyId },
        });
        const surveyForSubmit = result.data?.survey_by_pk as any;

        // Submit each answer
        for (const [questionId, answerValue] of Object.entries(surveyAnswers)) {
          if (answerValue !== null && answerValue !== undefined && answerValue !== "") {
            const question = surveyForSubmit?.questions?.find(
              (sq: { question?: { question_id?: string } }) =>
                sq.question?.question_id === questionId
            );

            if (Array.isArray(answerValue)) {
              // Multiple choice
              const options = (question?.question as any)?.options;
              if (options) {
                const sortedOptions = [...options].sort(
                  (a: { order?: number }, b: { order?: number }) =>
                    (a.order || 0) - (b.order || 0)
                );
                for (const optionIndex of answerValue) {
                  const selectedOption = sortedOptions[optionIndex];
                  if (selectedOption?.option_id) {
                    await addAnswer({
                      variables: {
                        answer: selectedOption.text,
                        questionId: questionId,
                        optionId: selectedOption.option_id,
                        userId: user.user_id,
                        createdAt: submissionTimestamp,
                      } as any,
                    });
                  }
                }
              }
            } else if (typeof answerValue === "number") {
              // Single choice
              const options = (question?.question as any)?.options;
              if (options) {
                const sortedOptions = [...options].sort(
                  (a: { order?: number }, b: { order?: number }) =>
                    (a.order || 0) - (b.order || 0)
                );
                const selectedOption = sortedOptions[answerValue];
                if (selectedOption) {
                  await addAnswer({
                    variables: {
                      answer: selectedOption.text,
                      questionId: questionId,
                      optionId: selectedOption.option_id,
                      userId: user.user_id,
                      createdAt: submissionTimestamp,
                    } as any,
                  });
                }
              }
            } else {
              // Open ended
              await addAnswer({
                variables: {
                  answer: String(answerValue),
                  questionId: questionId,
                  optionId: null,
                  userId: user.user_id,
                  createdAt: submissionTimestamp,
                } as any,
              });
            }
          }
        }
      }

      notify({
        message: "All surveys submitted successfully!",
        severity: NotificationSeverity.success,
      });

      navigate("/campaigns");
    } catch (error: any) {
      notify({
        message: error.message || "Error submitting surveys",
        severity: NotificationSeverity.error,
      });
    }
  };

  if (campaignLoading || surveyLoading) {
    return (
      <MainLayoutContainer title="Campaign Surveys">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </MainLayoutContainer>
    );
  }

  if (!campaign || surveys.length === 0) {
    return (
      <MainLayoutContainer title="Campaign Surveys">
        <Typography>No surveys found for this campaign.</Typography>
      </MainLayoutContainer>
    );
  }

  const sortedQuestions = [...(currentSurvey?.questions || [])].sort(
    (a, b) => (a.question?.order || 0) - (b.question?.order || 0)
  );

  return (
    <MainLayoutContainer title={`Campaign: ${campaign.campaign_id?.slice(0, 8)}...`}>
      <Box mt={2}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {surveys.map((survey, index) => (
            <Step key={survey.survey_id}>
              <StepLabel>{survey.survey?.name || `Survey ${index + 1}`}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {currentSurvey?.name || "Survey"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Step {activeStep + 1} of {surveys.length}
                </Typography>
              </Box>

              <Divider />

              {sortedQuestions.map((sq, index) => {
                const questionId = sq.question?.question_id || "";
                const questionType = sq.question?.type || "open_ended";
                const currentAnswer = currentAnswers[questionId];

                return (
                  <Box key={sq.survey_question_id || index}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Chip
                          label={`Question ${index + 1}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </Stack>

                      <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {sq.question?.title || "Untitled Question"}
                        </Typography>
                        {sq.question?.body && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {sq.question.body}
                          </Typography>
                        )}
                      </Box>

                      <Divider />

                      <Box>
                        {questionType === "open_ended" && (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            placeholder="Type your answer here..."
                            value={(currentAnswer as string) || ""}
                            onChange={(e) => updateAnswer(questionId, e.target.value)}
                          />
                        )}

                        {questionType === "single_choice" && (
                          <SingleChoiceSelector
                            value={currentAnswer as number}
                            onChange={(value) => updateAnswer(questionId, value)}
                            options={sq.question?.options || []}
                          />
                        )}

                        {questionType === "multiple_choice" && (
                          <SingleChoiceSelector
                            selectedValues={currentAnswer as number[]}
                            onMultipleChange={(values) =>
                              updateAnswer(questionId, values)
                            }
                            options={sq.question?.options || []}
                            multiple={true}
                          />
                        )}

                        {(questionType === "likert_scale" || questionType === "likert") && sq.question?.options && (
                          <LikertScaleSelector
                            value={currentAnswer !== undefined && currentAnswer !== null ? (currentAnswer as number) : null}
                            onChange={(value) => updateAnswer(questionId, value)}
                            options={sq.question.options}
                          />
                        )}
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>

          {activeStep < surveys.length - 1 ? (
            <Button onClick={handleNext} variant="contained">
              Next Survey
            </Button>
          ) : (
            <Button onClick={handleSubmit} variant="contained" color="success">
              Submit All Surveys
            </Button>
          )}
        </Box>
      </Box>
    </MainLayoutContainer>
  );
};
